<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, defineEmits, defineProps, watch } from 'vue'

const emits = defineEmits(['toggle'])
const props = defineProps({ open: { type: Boolean, required: true } })

const isOpen = ref(props.open)
const emberVisible = ref(false)

watch(() => props.open, (newVal) => {
  isOpen.value = newVal
})

watch(isOpen, (val) => {
  if (val) {
    setTimeout(() => {
      emberVisible.value = true
    }, 900) // 0.6s+0.3s for bar animations
  } else {
    emberVisible.value = false
  }
})

function toggleMenu() {
  isOpen.value = !isOpen.value
  emits('toggle', isOpen.value)
}

function onClickOutside(event: MouseEvent) {
  const menu = document.getElementById('hamburger-menu')
  const button = event.target as HTMLElement
  if (
    isOpen.value &&
    menu &&
    !menu.contains(event.target as Node) &&
    !button.closest('.hamburger-button')
  ) {
    isOpen.value = false
    emits('toggle', isOpen.value)
  }
}

onMounted(() => {
  document.addEventListener('click', onClickOutside)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside)
})
</script>

<template>
  <div class="hamburger-wrapper" :class="{ open: isOpen }">
    <button
      class="hamburger-button"
      @click="toggleMenu"
      aria-label="Toggle menu"
:aria-expanded="isOpen ? 'true' : 'false'"
      aria-controls="hamburger-menu"
      type="button"
    >
      <span class="bar"></span>
      <span class="bar"></span>
      <span class="bar"></span>
      <span class="ember-orb" :class="{ visible: emberVisible }"></span>
    </button>
    <transition name="slide-drawer">
      <nav
        v-show="isOpen"
        id="hamburger-menu"
        class="hamburger-menu-drawer"
        role="menu"
      >
        <slot name="menu" />
      </nav>
    </transition>
  </div>
</template>

<style>
.hamburger-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 50px;
  height: 50px;
  z-index: 1002;
}

.hamburger-button {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 45px;
  background: #b82207;
  border: none;
  border-radius: 1rem;
  cursor: pointer;
  padding: 0.32rem;
  box-sizing: border-box;
  box-shadow:
    inset 0 2.4px 5px rgba(255,90,20,0.19),
    0 3.2px 7px rgba(180,35,10,0.22),
    0 0 14px 2.5px rgba(238,83,20,0.13);
  transition: background 0.18s, box-shadow 0.22s;
}
.hamburger-button:hover {
  background: #d93b12;
  box-shadow:
    0 6px 24px rgba(235, 71, 0, 0.25),
    0 0 18px 3.2px rgba(255, 83, 20, 0.11);
}
.hamburger-button:active {
  background: #892004;
  box-shadow:
    0 7.6px 20px rgba(125, 15, 2, 0.27),
    0 0 12px 2.5px rgba(255, 90, 20, 0.18);
}
.hamburger-button:focus {
  outline: 2px solid #ffae1c;
  outline-offset: 2px;
}

.bar {
  display: block;
  width: 28.3px;
  height: 3.8px;
  margin: 4.1px 0;
  background: linear-gradient(90deg, #ff8635, #ff4e11 60%, #ffd1a2 100%);
  border-radius: 2px;
  box-shadow:
    0 0 3px 0.5px rgba(255, 90, 20, 0.22),
    0 0 6px 1.5px rgba(255, 140, 40, 0.17);
  transition: all 0.24s cubic-bezier(.45,.2,.35,1.2), box-shadow 0.24s ease-in-out;
  animation: emberFlicker 3.7s infinite;
}

@keyframes emberFlicker {
  0%, 100% {
    box-shadow:
      0 0 3px 0.5px rgba(255, 90, 20, 0.22),
      0 0 6px 1.5px rgba(255, 140, 40, 0.17);
  }
  50% {
    box-shadow:
      0 0 4px 1.2px rgba(235, 140, 60, 0.25),
      0 0 8px 2.2px rgba(255, 83, 20, 0.14);
  }
}

@keyframes burnFadeUp {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
    box-shadow:
      0 0 3px 0.5px rgba(255, 90, 20, 0.22),
      0 0 6px 1.5px rgba(255, 140, 40, 0.17);
  }
  100% {
    opacity: 0;
    transform: translateY(-12px) scale(1.15);
    filter: blur(2px);
    box-shadow: none;
  }
}
.hamburger-wrapper.open .bar:nth-child(1) {
  animation: burnFadeUp 0.6s forwards;
  animation-delay: 0s;
}
.hamburger-wrapper.open .bar:nth-child(2) {
  animation: burnFadeUp 0.6s forwards;
  animation-delay: 0.15s;
}
.hamburger-wrapper.open .bar:nth-child(3) {
  animation: burnFadeUp 0.6s forwards;
  animation-delay: 0.3s;
}
.hamburger-wrapper:not(.open) .bar {
  animation: none;
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0);
}

/* EMBER ORB */
.ember-orb {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 23px;
  height: 23px;
  background: radial-gradient(circle at center, #ff5400 0%, #e35319 65%, transparent 98%);
  border-radius: 50%;
  box-shadow:
    0 0 10px 4px #ff5a1b,
    0 0 21px 9px #db2f00;
  transform: translate(-50%, -50%) scale(0.72);
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 0.34s cubic-bezier(.4,1.45,.37,1.11),
    filter 0.28s cubic-bezier(.4,1.45,.37,1.11);
  filter: blur(0.8px) brightness(0.97);
  z-index: 1;
}
.ember-orb.visible {
  opacity: 1;
  animation: emberGlow 19s cubic-bezier(.44, 0.07, 0.47, 1) infinite alternate;
  filter: blur(1.15px) brightness(1.08);
}
@keyframes emberGlow {
  0%, 100% {
    filter: blur(1px) brightness(.92);
    box-shadow:
      0 0 13px 6px #e86b15,
      0 0 24px 9px #d13700;
  }
  62% {
    filter: blur(1.2px) brightness(1.00);
    box-shadow:
      0 0 20px 9px #f86204,
      0 0 32px 13px #c55a0e;
  }
  87% {
    filter: blur(.8px) brightness(.87);
    box-shadow:
      0 0 10px 4px #b94000,
      0 0 20px 6px #a42804;
  }
}

/* --- SLIDE OUT DRAWER (restored, with original color, smooth animation) --- */
.hamburger-menu-drawer {
  position: fixed;
  top: 10vh;
  left: 0;
  height: vh;
  width: 78vw;
  max-width: 300px;
  background: rgba(150, 5, 0, 0.87); /* deep, original */
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  border-right: 1px solid rgba(255, 255, 255, 0.10);
  box-shadow: 2.5px 0 18px 0 rgba(150, 5, 0, 0.23);

  z-index: 1500;
  padding: 2.2rem 0 0.7rem 0;

  display: flex;
  flex-direction: column;

  transition:
    transform 0.44s cubic-bezier(.31,1.31,.43,1.12),
    opacity 0.27s cubic-bezier(.31,1.31,.43,1.12);
    overflow: hidden;
}

/* Much smoother slide/fade for the nav */
.slide-drawer-enter-active,
.slide-drawer-leave-active {
  transition:
    transform 0.44s cubic-bezier(.31,1.31,.43,1.12),
    opacity 0.27s cubic-bezier(.31,1.31,.43,1.12);
}
.slide-drawer-enter-from,
.slide-drawer-leave-to {
  transform: translateX(-125%);
  opacity: 0;
}
.slide-drawer-enter-to,
.slide-drawer-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.hamburger-menu-drawer .menu-link,
.hamburger-menu-drawer a.menu-link {
  color: #ffb059 !important;
  font-weight: 800;
  font-size: 1.2rem;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: none;
  opacity: 1 !important;
  text-decoration: none;
  display: block;
  padding: 1.5em 1.8em;
  border-radius: 8px;
  letter-spacing: 0.04em;
  margin-bottom: 0.3rem;
}
.hamburger-menu-drawer .menu-link:hover,
.hamburger-menu-drawer .menu-link.router-link-exact-active {
  color: #ffe6c7;
  background: rgba(255, 120, 25, 0.12);
}

/* Responsive sizes (unchanged) */
@media (min-width: 700px) {
  .hamburger-wrapper {
    width: 51.6px;
    height: 51.6px;
  }
  .hamburger-button {
    width: 46.5px;
    height: 46.5px;
    border-radius: 1.16rem;
  }
  .bar {
    width: 30px;
    height: 4.17px;
    margin: 5px 0;
  }
  .hamburger-menu-drawer {
    max-width: 330px;
    padding: 3.3rem 0 1.5rem 0;
  }
  .hamburger-menu-drawer .menu-link,
  .hamburger-menu-drawer a.menu-link {
    font-size: 1.18rem;
    padding: 1.22em 2em;
  }
}

</style>

